import { Input, Selector } from '@casumo/dojo-react';
import { ChevronDown, Close, Search } from '@casumo/dojo-react-icons';
import classNames from 'classnames';
import { getAlpha2Code, getName } from 'i18n-iso-countries';
import { memo, useMemo, useState } from 'react';
import useCommonTranslations from '../api/hooks/useCommonTranslations.tsx';
import useCountriesData, { TCountry } from '../api/hooks/useCountriesData.tsx';
import Drawer from '../design-system/drawer.tsx';
import Skeleton from '../design-system/skeleton.tsx';
import { useRegistrationStore } from '../store/store.ts';
import { TCountrySelectorField } from '../types/types.ts';
import { TEST_ID } from '../utils/constants.ts';
import { COUNTRY_ALIASES, getAcceptedCountryCodes } from '../utils/countries.ts';
import { GtmEvents } from '../utils/gtm/gtm.ts';
import { filterAndSortCountries } from '../utils/utils.ts';

const CountrySelector = ({ name, label, validation, disabled, dialogTitle, onBlur }: TCountrySelectorField & {
  onChange: (e: string) => void;
  onBlur?: (field: Partial<TCountrySelectorField>) => void;
}) => {
  const { formData, setFormData, errors, setErrors, metadata } = useRegistrationStore();
 
  const {
    commonTranslations,
    isLoading: commonTranslationsIsLoading,
  } = useCommonTranslations();

  const { countries: allCountries, isLoading: countriesDataIsLoading, error: countriesDataError } = useCountriesData();

  const getCountryAlpha2Code = (country: TCountry): string =>
    country.countryCodeIso || (getAlpha2Code(country?.name, 'en') as string);

  const countries = useMemo(
    () => name === 'countryOfResidence'
      ? allCountries
        .filter((country) => getAcceptedCountryCodes()
          .includes(getCountryAlpha2Code(country)))
        .sort((a, b) => a.name.localeCompare(b.name))
      : allCountries,
    [allCountries, name]);

  const [isOpen, setIsOpen] = useState(false);
  const [country, setCountry] = useState({
    name: getName(formData?.[name] || formData?.countryOfResidence, 'en'),
    flag: '',
    code: '',
    iso:  '',
  });

  const [searchValue, setSearchValue] = useState('');

  const handleIsOpen = (isOpen: boolean) => {
    setIsOpen(isOpen);
    if (isOpen) GtmEvents.logCountrySelector(metadata?.currentStep);
  };
  const getCountryCode = (name: string, iso: string): string | undefined => {
    
    if (iso) return iso;
    if (!name) return undefined;
    let code = getAlpha2Code(name, 'en');
    if (code) return code;
  
    const alias = COUNTRY_ALIASES[name];
  
    if (alias) {
      code = getAlpha2Code(alias, 'en');
    }
  
    return code;
  };
 
  const handleCheck = (checked: { name: string; flag: string; code: string; iso: string}) => {

    setErrors({ [name]: '' });
    setCountry({ name: checked.name, flag: checked.flag, code: checked.code, iso: checked.iso });

    const selectedCountryCode = getCountryCode(checked.name, checked.iso);
    setFormData({
      [name]: selectedCountryCode,
    });

    if (name === 'countryOfResidence') {
      setFormData({
        phonePrefix:    checked.code,
        nationality:    selectedCountryCode,
        countryOfBirth: selectedCountryCode,
      });
    }

    if (name === 'nationality') {
      setFormData({
        countryOfBirth: selectedCountryCode,
      });
    }

    setIsOpen(false);
  };

  const filteredCountries = useMemo(
    () => filterAndSortCountries(countries, searchValue),
    [countries, searchValue],
  );

  if (countriesDataIsLoading || countriesDataError || commonTranslationsIsLoading) {
    return <Skeleton height="h-[52px]" />;
  }

  const readOnlyInput = () => (
    <Input
      className={classNames({
        'cursor-pointer':     !disabled,
        'cursor-not-allowed': disabled,
      })}
      id="countrySelectorInput"
      name={name}
      label={label}
      value={getName(formData?.[name], 'en') || ''}
      disabled={disabled}
      onKeyDown={e => e.key === 'Enter' && handleIsOpen(true)}
      readOnly
      prefix={getName(formData?.[name], 'en') || formData?.countryOfResidence
        ?
          <img
            src={country.iso
              ? allCountries.find(c => c.countryCodeIso === country.iso)?.flag
              : allCountries.find(c => c.countryCodeIso === formData?.countryOfResidence)?.flag}
            alt={country.name}
          />
        : null
      }
      sufix={
        !disabled
          ? <span className="text-[24px]"><ChevronDown /></span>
          : null
      }
      onClick={() => handleIsOpen(true)}
      onBlur={() => onBlur?.({ name, validation })}
      errorMessage={errors[name]}
      error={errors[name] ? true : undefined}
      data-error={errors[name] ? true : undefined}
      data-testid={TEST_ID.COMBOBOX_TRIGGER + name}
    />
  );

  return (
    <>
      {readOnlyInput()}
      <Drawer
        isOpen={isOpen}
        setIsOpen={handleIsOpen}>
        <div className="flex flex-col h-full w-full p-md overflow-hidden">
          <div className="flex justify-between text-white pb-sm font-bold">
            <h1 className="text-h-lg">{dialogTitle}</h1>
            <span className="p-2xs cursor-pointer text-[24px]" onClick={() => handleIsOpen(false)}><Close /></span>
          </div>
          <Input
            id="countrySearch"
            name="search"
            placeholder={commonTranslations?.search ?? 'search'}
            variant="dark"
            onChange={(e) => setSearchValue(e.currentTarget.value)}
            value={searchValue}
            prefix={<span className="text-[24px]"><Search /></span>}
            onClear={() => setSearchValue('')}
            clearable
            autoFocus
            data-testid={TEST_ID.SEARCH + name}
          />
          <Selector name="country" direction="vertical" variant="dark"
            className="flex-1 w-full mt-md p-[2px] overflow-y-auto">
            {filteredCountries.map((country, idx) => (
              <Selector.Radio
                className="flex gap-2xs"
                key={idx}
                value={country.callingCode}
                onClick={() => handleCheck({
                  name: getName(country?.countryCodeIso, 'en') || country.name,
                  flag: country.flag,
                  code: country.callingCode,
                  iso:  country.countryCodeIso,
                })}
                onKeyDown={e => e.key === 'Enter' && console.info(country)}
                data-testid={TEST_ID.COUNTRY + country.name}
              >
                <img
                  src={country.flag}
                  alt={country.name}
                  className='h-md w-md'
                />
                {getName(country?.countryCodeIso, 'en') || country.name}
              </Selector.Radio>
            ),
            )}
          </Selector>
        </div>
      </Drawer>
    </>
  );
};

const Memoized = memo(CountrySelector);
Memoized.displayName = 'CountrySelector';

export default Memoized;
